import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { serverError } from "./registerPaymentModal";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import { useEnviarAprobacion, useTieneCaja } from "../../../aprobaciones/enviarAprobacion";
import { dineroCorto } from "../../../../utils/dineroCorto";

/*
	Descuentos especiales (Cobros unificado, Etapa 1): la cajera los sugiere desde "Registrar pago" y Supervisión de
	Caja del plantel los acepta o rechaza aquí (llega por Cliq con el enlace /payments?descuento=N). Solo un
	descuento aceptado se puede aplicar, y una sola vez.
	Etapa 2: aquí mismo llegan las sugerencias de eximir un recargo; aceptarla exime el recargo en ese momento.
*/
export interface DiscountRequestsModalProps { campusId: number; currencySymbol: string; focusId?: number | null; onClose: () => void; }

const money = (n: number, s: string) => dineroCorto(n, s);
const when = (t: string | null) => fechaHoraAmPm(t);   // dd/mm/aaaa y am/pm (Esteban: nunca 14:24)

const DiscountRequestsModal: React.FC<DiscountRequestsModalProps> = ({ campusId, currencySymbol, focusId, onClose }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [data, setData] = useState<any[] | null>(null);
	const [canDecide, setCanDecide] = useState<boolean>(false);
	const [onlyPending, setOnlyPending] = useState<boolean>(!focusId);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [rejecting, setRejecting] = useState<number | null>(null);
	const [notes, setNotes] = useState<string>("");
	// Fase 2: Caja que no decide aquí pide que se acepte y queda Por Aprobar para Supervisión de Caja.
	const tieneCaja = useTieneCaja();
	const ap = useEnviarAprobacion(campusId);
	const pedir = (r: any) => ap.pedir({
		kind: "descuento_especial", titulo: r.kind === "surcharge_waiver" ? "Eximir Recargo" : "Descuento Especial",
		data: { requestId: r.id, accept: true, notes: "" }, onDone: () => { load(); },
	});

	const load = () => http.paymentService.discountRequests(campusId, onlyPending ? 1 : undefined)
		.then((r: any) => { setData(r.data.data || []); setCanDecide(!!r.data.canDecide); })
		.catch((e: any) => setError(serverError(e)));
	useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId, onlyPending]);

	const decide = async (id: number, accept: boolean) => {
		try {
			setBusy(true);
			setError(null);
			await http.paymentService.decideDiscount(campusId, id, accept, notes.trim());
			setRejecting(null);
			setNotes("");
			await load();
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<>
		<Modal className="waModulo" show onHide={onClose} size="lg" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>Descuentos Especiales y Recargos por Eximir</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 14 }}>
				{error ? <div className="alert alert-danger">{error}</div> : null}
				<label style={{ position: "static", display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--wa-apagado)" }}>
					<input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} /> Solo Pendientes
				</label>
				{data === null ? <div className="loader" /> : data.length < 1 ? <div style={{ padding: 10, color: "var(--wa-apagado)" }}>Nada por aquí.</div> : data.map((r: any) => (
					<div key={"dr" + r.id} style={{ border: "1px solid " + (r.id === focusId ? "var(--wa-acento)" : "var(--wa-filo)"), padding: 10, marginTop: 8,
						background: r.id === focusId ? "var(--wa-acento-suave)" : undefined }}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
							<b>#{r.id} · {r.studentName}</b>
							<span style={{ fontWeight: 700, color: r.statusId === 1 ? "var(--wa-oro)" : r.statusId === 2 ? "var(--wa-acento-texto)" : "var(--wa-apagado)" }}>{r.status}</span>
						</div>
						{r.kind === "surcharge_waiver"
							? <div><b>Eximir Recargo</b> · {r.concept} · {r.campusName} · debe {money(r.listAmount, currencySymbol)} → eximir <b>{r.calcType === "percent" ? r.value + "%" : money(r.value, currencySymbol)}</b> ({money(r.discountAmount, currencySymbol)}){r.statusId === 1 ? <span style={{ color: "var(--wa-apagado)" }}> · al aceptar se exime en ese momento</span> : null}</div>
							: <div>{r.concept} · {r.campusName} · lista {money(r.listAmount, currencySymbol)} → descuento <b>{r.calcType === "percent" ? r.value + "%" : money(r.value, currencySymbol)}</b> ({money(r.discountAmount, currencySymbol)})</div>}
						<div style={{ whiteSpace: "pre-wrap" }}><span style={{ color: "var(--wa-apagado)" }}>Motivo: </span>{r.reason}</div>
						<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>Pidió {r.requestedByName} · {when(r.requestedAt)}{r.intentId ? " · ticket #" + r.intentId : ""}
							{r.decidedByName ? " · decidió " + r.decidedByName + " · " + when(r.decidedAt) : ""}{r.decisionNotes ? ": " + r.decisionNotes : ""}</div>
						{canDecide && r.statusId === 1 ? (
							rejecting === r.id ? (
								<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr" : "1fr auto auto", gap: 6, marginTop: 8 }}>
									<input className="form-control form-control-sm" autoFocus placeholder="Por qué se rechaza" value={notes} onChange={(e) => setNotes(e.target.value)} />
									<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setRejecting(null)}>Atrás</button>
									<button type="button" className="btn btn-warning btn-sm" disabled={busy || notes.trim().length < 5} onClick={() => decide(r.id, false)}>Rechazar</button>
								</div>
							) : (
								<div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
									<button type="button" className="btn btn-warning btn-sm" disabled={busy} onClick={() => { setRejecting(r.id); setNotes(""); }}>Rechazar</button>
									<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => decide(r.id, true)}>Aceptar</button>
								</div>
							)
						) : tieneCaja && r.statusId === 1 ? (
							<div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
								<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => pedir(r)}>Enviar a Aprobación</button>
							</div>
						) : null}
					</div>
				))}
			</Modal.Body>
		</Modal>
		{ap.modal}
		</>
	);
};

export default DiscountRequestsModal;
